import { Injectable, computed, inject, signal } from '@angular/core';
import { firstValueFrom } from 'rxjs';
import {
  ApplyCouponCode,
  AssignPaymentMethod,
  AssignPaymentMethodRetry,
  AssignShippingAddress,
  OrderApiService,
  RemoveCouponCode
} from './order-api.service';

import { ListResponse } from 'src/app/core/models/list-response.model';
import { Quotation } from 'src/app/core/models/quotation.model';
import { AvailableOffer, Order } from '../model/order.model';
import { ShippingCharge } from '../model/shipping-charge.interface';
import { TrackShipment } from '../model/track-shipment.model';

@Injectable({
  providedIn: 'root'
})
export class OrderRepositoryService {
  private orderApiService = inject(OrderApiService);

  // ===== SERVICE STATE MANAGEMENT =====
  private _isLoading = signal<boolean>(false);
  private _lastAction = signal<string>('');

  constructor() {
    // Auto-initialize when service is first injected (Angular 20 best practice)
  }

  // ===== DATA STATE SIGNALS =====
  private _order = signal<Order | null>(null);
  readonly order = this._order.asReadonly();

  private _quotation = signal<Quotation | null>(null);
  readonly quotation = this._quotation.asReadonly();

  // Additional signals for other data
  private _orders = signal<ListResponse<Order> | null>(null);
  readonly orders = this._orders.asReadonly();

  private _orderForRetry = signal<Order | null>(null);
  readonly orderForRetry = this._orderForRetry.asReadonly();

  private _paymentMethods = signal<ListResponse<any> | null>(null);
  readonly paymentMethods = this._paymentMethods.asReadonly();

  private _paymentMethodsForRetry = signal<ListResponse<any> | null>(null);
  readonly paymentMethodsForRetry = this._paymentMethodsForRetry.asReadonly();

  private _trackShipment = signal<TrackShipment | null>(null);
  readonly trackShipment = this._trackShipment.asReadonly();

  private _availableOffers = signal<AvailableOffer[] | null>(null);
  readonly availableOffers = this._availableOffers.asReadonly();

  private _paymentStatus = signal<{ payment_success: boolean } | null>(null);
  readonly paymentStatus = this._paymentStatus.asReadonly();

  private _recheckPaymentError = signal<string | null>(null);
  readonly recheckPaymentError = this._recheckPaymentError.asReadonly();

  private _shippingCharges = signal<ListResponse<ShippingCharge> | null>(null);
  readonly shippingCharges = this._shippingCharges.asReadonly();

  private _orderPayments = signal<any | null>(null);
  readonly orderPayments = this._orderPayments.asReadonly();

  // Loading and error states
  private _isLoadingOrders = signal<boolean>(false);
  readonly isLoadingOrders = this._isLoadingOrders.asReadonly();

  private _ordersError = signal<string | null>(null);
  readonly ordersError = this._ordersError.asReadonly();

  // ===== PUBLIC READONLY SIGNALS =====
  readonly isLoading = this._isLoading.asReadonly();
  readonly lastAction = this._lastAction.asReadonly();


  // Computed signals for derived state
  readonly hasOrder = computed(() => !!this._order());
  readonly hasQuotation = computed(() => !!this._quotation());
  readonly hasOrders = computed(() => !!this._orders());
  readonly hasOrderForRetry = computed(() => !!this._orderForRetry());
  readonly hasPaymentMethods = computed(() => !!this._paymentMethods());
  readonly hasPaymentMethodsForRetry = computed(() => !!this._paymentMethodsForRetry());
  readonly hasTrackShipment = computed(() => !!this._trackShipment());
  readonly hasAvailableOffers = computed(() => !!this._availableOffers());
  readonly hasPaymentStatus = computed(() => !!this._paymentStatus());
  readonly hasShippingCharges = computed(() => !!this._shippingCharges());
  readonly hasOrderPayments = computed(() => !!this._orderPayments());
  readonly orderStatus = computed(() => this._order()?.status);
  readonly quotationStatus = computed(() => this._quotation()?.status);


  // ===== UTILITY METHODS =====
  resetService(): void {
    this._isLoading.set(false);
    this._lastAction.set('');
    this.clearAllData();
  }

  getServiceStatus(): {
    isLoading: boolean;
  } {
    return {
      isLoading: this._isLoading()
    };
  }

  clearAllData(): void {
    this._order.set(null);
    this._quotation.set(null);
    this._orders.set(null);
    this._orderForRetry.set(null);
    this._paymentMethods.set(null);
    this._paymentMethodsForRetry.set(null);
    this._trackShipment.set(null);
    this._availableOffers.set(null);
    this._paymentStatus.set(null);
    this._recheckPaymentError.set(null);
    this._shippingCharges.set(null);
    this._orderPayments.set(null);
    this._ordersError.set(null);
  }

  clearError(): void {
  }

  setError(error: string): void {
    // Order operation failed
  }

  // Public methods to update signals
  setQuotation(quotation: Quotation | null) {
    this._quotation.set(quotation);
  }

  setOrder(order: Order | null) {
    this._order.set(order);
  }

  setOrders(orders: ListResponse<Order> | null) {
    this._orders.set(orders);
  }

  setOrderForRetry(order: Order | null) {
    this._orderForRetry.set(order);
  }

  setPaymentMethods(paymentMethods: ListResponse<any> | null) {
    this._paymentMethods.set(paymentMethods);
  }

  setPaymentMethodsForRetry(paymentMethods: ListResponse<any> | null) {
    this._paymentMethodsForRetry.set(paymentMethods);
  }

  setTrackShipment(trackShipment: TrackShipment | null) {
    this._trackShipment.set(trackShipment);
  }

  setAvailableOffers(availableOffers: AvailableOffer[] | null) {
    this._availableOffers.set(availableOffers);
  }

  setPaymentStatus(paymentStatus: { payment_success: boolean } | null) {
    this._paymentStatus.set(paymentStatus);
  }

  setShippingCharges(shippingCharges: ListResponse<ShippingCharge> | null) {
    this._shippingCharges.set(shippingCharges);
  }

  setOrderPayments(orderPayments: any | null) {
    this._orderPayments.set(orderPayments);
  }

  // Signal-based methods that return Promise<Quotation | null> to match old Observable behavior
  async assignBillingAddress(reqObj: any): Promise<Quotation | null> {
    this._isLoading.set(true);
    this._lastAction.set('assignBillingAddress');

    try {
      const quotation = await firstValueFrom(this.orderApiService.assignBillingAddress(reqObj));
      this._quotation.set(quotation);
      return quotation; // Return the response like the old Observable did
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Failed to assign billing address';
      // Order operation failed
      return null; // Return null on error like the old Observable did
    } finally {
      this._isLoading.set(false);
    }
  }

  async assignShippingAddress(reqObj: AssignShippingAddress): Promise<void> {
    this._isLoading.set(true);
    this._lastAction.set('assignShippingAddress');

    try {
      const quotation = await firstValueFrom(this.orderApiService.assignShippingAddress(reqObj));
      this._quotation.set(quotation);
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Failed to assign shipping address';
      // Order operation failed
    } finally {
      this._isLoading.set(false);
    }
  }

  async assignPaymentMethod(reqObj: AssignPaymentMethod): Promise<void> {
    this._isLoading.set(true);
    this._lastAction.set('assignPaymentMethod');

    try {
      const quotation = await firstValueFrom(this.orderApiService.assignPaymentMethod(reqObj));
      this._quotation.set(quotation);
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Failed to assign payment method';
      // Order operation failed
    } finally {
      this._isLoading.set(false);
    }
  }

  async assignPaymentMethodRetry(reqObj: AssignPaymentMethodRetry): Promise<Order> {
    this._isLoading.set(true);
    this._lastAction.set('assignPaymentMethodRetry');

    try {
      const order = await firstValueFrom(this.orderApiService.assignPaymentMethodRetry(reqObj));
      this._orderForRetry.set(order); // Update signal for other components that might watch it
      return order; // Return order for direct use
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Failed to assign payment method retry';
      throw error; // Re-throw for caller to handle
    } finally {
      this._isLoading.set(false);
    }
  }

  async validatePaymentMethodRetry(reqObj: AssignPaymentMethodRetry): Promise<Order> {
    this._isLoading.set(true);
    this._lastAction.set('validatePaymentMethodRetry');

    try {
      const order = await firstValueFrom(this.orderApiService.validatePaymentMethodRetry(reqObj));
      this._orderForRetry.set(order); // Update signal for other components that might watch it
      return order; // Return order for direct use
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Failed to validate payment method retry';
      throw error; // Re-throw for caller to handle
    } finally {
      this._isLoading.set(false);
    }
  }

  async placeOrder(quotationId: string, quotationDetail: any): Promise<void> {
    this._isLoading.set(true);
    this._lastAction.set('placeOrder');

    try {
      const order = await firstValueFrom(this.orderApiService.placeOrder(quotationId, quotationDetail));
      this._order.set(order);
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Failed to place order';
      // Order operation failed
    } finally {
      this._isLoading.set(false);
    }
  }

  async getOrders(pageNumber = 0): Promise<void> {
    this._isLoading.set(true);
    this._lastAction.set('getOrders');

    try {
      this._isLoadingOrders.set(true);
      this._ordersError.set(null);
      const orders = await firstValueFrom(this.orderApiService.getOrders(pageNumber));
      this._orders.set(orders);
    } catch (error) {
      this._orders.set(null);
      const errorMessage = error instanceof Error ? error.message : 'Failed to load orders';
      this._ordersError.set(errorMessage);
      // Order operation failed
    } finally {
      this._isLoading.set(false);
      this._isLoadingOrders.set(false);
    }
  }

  async getOrder(orderId: string): Promise<void> {
    this._isLoading.set(true);
    this._lastAction.set('getOrder');

    try {
      const order = await firstValueFrom(this.orderApiService.getOrder(orderId));
      this._order.set(order);
    } catch (error) {
      this._order.set(null);
      const errorMessage = error instanceof Error ? error.message : 'Failed to get order';
      // Order operation failed
    } finally {
      this._isLoading.set(false);
    }
  }

  async getOrderForRetryPayment(orderId: string): Promise<void> {
    this._isLoading.set(true);
    this._lastAction.set('getOrderForRetryPayment');

    try {
      const order = await firstValueFrom(this.orderApiService.getOrderForRetryPayment(orderId));
      this._orderForRetry.set(order);
    } catch (error) {
      this._orderForRetry.set(null);
      const errorMessage = error instanceof Error ? error.message : 'Failed to get order for retry payment';
      // Order operation failed
    } finally {
      this._isLoading.set(false);
    }
  }

  async getPaymentMethods(quotationId: string): Promise<void> {
    this._isLoading.set(true);
    this._lastAction.set('getPaymentMethods');

    try {
      const paymentMethods = await firstValueFrom(this.orderApiService.getPaymentMethods(quotationId));
      this._paymentMethods.set(paymentMethods);
    } catch (error) {
      this._paymentMethods.set(null);
      const errorMessage = error instanceof Error ? error.message : 'Failed to get payment methods';
      // Order operation failed
    } finally {
      this._isLoading.set(false);
    }
  }

  async getPaymentMethodsForRetry(orderId: string): Promise<void> {
    this._isLoading.set(true);
    this._lastAction.set('getPaymentMethodsForRetry');

    try {
      const paymentMethods = await firstValueFrom(this.orderApiService.getPaymentMethodsForRetry(orderId));
      this._paymentMethodsForRetry.set(paymentMethods);
    } catch (error) {
      this._paymentMethodsForRetry.set(null);
      const errorMessage = error instanceof Error ? error.message : 'Failed to get payment methods for retry';
      // Order operation failed
    } finally {
      this._isLoading.set(false);
    }
  }

  async applyCouponCode(reqObj: ApplyCouponCode): Promise<void> {
    this._isLoading.set(true);
    this._lastAction.set('applyCouponCode');

    try {
      const quotation = await firstValueFrom(this.orderApiService.applyCouponCode(reqObj));
      this._quotation.set(quotation);
    } catch (error: any) {
      throw error;
    } finally {
      this._isLoading.set(false);
    }
  }

  async removeCouponCode(reqObj: RemoveCouponCode): Promise<void> {
    this._isLoading.set(true);
    this._lastAction.set('removeCouponCode');

    try {
      const quotation = await firstValueFrom(this.orderApiService.removeCouponCode(reqObj));
      this._quotation.set(quotation);
    } catch (error: any) {
      throw error;
    } finally {
      this._isLoading.set(false);
    }
  }

  // Return the response data so component can check payment_success
  async updateOrderPaymentStatus(reqObj: any): Promise<any> {
    this._isLoading.set(true);
    this._lastAction.set('updateOrderPaymentStatus');

    try {
      const response = await firstValueFrom(this.orderApiService.updateOrderPaymentStatus(reqObj));

      this._order.set(response as any);

      return response; // Return full response for component to use
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Failed to update order payment status';
      throw error; // Re-throw so component can handle
    } finally {
      this._isLoading.set(false);
    }
  }

  async updateOrderPaymentCancelled(orderId: string): Promise<void> {
    this._isLoading.set(true);
    this._lastAction.set('updateOrderPaymentCancelled');

    try {
      const order = await firstValueFrom(this.orderApiService.updateOrderPaymentCancelled(orderId)) as Order;
      this._order.set(order);
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Failed to update order payment cancelled';
      // Order operation failed
    } finally {
      this._isLoading.set(false);
    }
  }

  async getTrackShipment(shipmentId: string): Promise<void> {
    this._isLoading.set(true);
    this._lastAction.set('getTrackShipment');

    try {
      const trackShipment = await firstValueFrom(this.orderApiService.trackShipment(shipmentId));
      this._trackShipment.set(trackShipment);
    } catch (error) {
      this._trackShipment.set(null);
      const errorMessage = error instanceof Error ? error.message : 'Failed to track shipment';
      // Order operation failed
    } finally {
      this._isLoading.set(false);
    }
  }

  async applyCashPoints(quotationId: string): Promise<void> {
    this._isLoading.set(true);
    this._lastAction.set('applyCashPoints');

    try {
      const quotation = await firstValueFrom(this.orderApiService.applyCashPoints(quotationId));
      this._quotation.set(quotation);
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Failed to apply cash points';
      // Order operation failed
    } finally {
      this._isLoading.set(false);
    }
  }

  async removeCashPoints(quotationId: string): Promise<void> {
    this._isLoading.set(true);
    this._lastAction.set('removeCashPoints');

    try {
      const quotation = await firstValueFrom(this.orderApiService.removeCashPoints(quotationId));
      this._quotation.set(quotation);
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Failed to remove cash points';
      // Order operation failed
    } finally {
      this._isLoading.set(false);
    }
  }

  async getAvailableOffers(quotationId: string): Promise<void> {
    this._isLoading.set(true);
    this._lastAction.set('getAvailableOffers');

    try {
      const availableOffers = await firstValueFrom(this.orderApiService.getAvailableOffers(quotationId));
      this._availableOffers.set(availableOffers);
    } catch (error) {
      this._availableOffers.set(null);
      const errorMessage = error instanceof Error ? error.message : 'Failed to get available offers';
      // Order operation failed
    } finally {
      this._isLoading.set(false);
    }
  }

  async refreshPaymentStatus(order_id: string): Promise<void> {
    this._isLoading.set(true);
    this._lastAction.set('refreshPaymentStatus');

    try {
      const paymentStatus = await firstValueFrom(this.orderApiService.refreshPaymentStatus(order_id));
      this._paymentStatus.set(paymentStatus);
    } catch (error) {
      this._paymentStatus.set(null);
      const errorMessage = error instanceof Error ? error.message : 'Failed to refresh payment status';
      // Order operation failed
    } finally {
      this._isLoading.set(false);
    }
  }

  async getShippingCharges(quotationId: string): Promise<void> {
    this._isLoading.set(true);
    this._lastAction.set('getShippingCharges');

    try {
      const shippingCharges = await firstValueFrom(this.orderApiService.getShippingCharges(quotationId));
      this._shippingCharges.set(shippingCharges);
    } catch (error) {
      this._shippingCharges.set(null);
      const errorMessage = error instanceof Error ? error.message : 'Failed to get shipping charges';
      // Order operation failed
    } finally {
      this._isLoading.set(false);
    }
  }

  async getOrderPayments(order_id: string): Promise<void> {
    this._isLoading.set(true);
    this._lastAction.set('getOrderPayments');

    try {
      const orderPayments = await firstValueFrom(this.orderApiService.getOrderPayments(order_id));
      this._orderPayments.set(orderPayments);
    } catch (error) {
      this._orderPayments.set(null);
      const errorMessage = error instanceof Error ? error.message : 'Failed to get order payments';
      // Order operation failed
    } finally {
      this._isLoading.set(false);
    }
  }

  async recheckOrderPayment(order_payment_id: string): Promise<void> {
    this._isLoading.set(true);
    this._lastAction.set('recheckOrderPayment');

    try {
      this._recheckPaymentError.set(null);
      const paymentResult = await firstValueFrom(this.orderApiService.recheckOrderPayment(order_payment_id));
      this._paymentStatus.set(paymentResult);
    } catch (error) {
      this._paymentStatus.set(null);
      const errorMessage = error instanceof Error ? error.message : 'Failed to recheck payment';
      this._recheckPaymentError.set(errorMessage);
      // Order operation failed
    } finally {
      this._isLoading.set(false);
    }
  }
}
